import { ConverterMap } from './shared';
import * as helpers from './helpers';
import * as customvars from './customvars';
import { SearchOperator as Op } from '@fullstory/client-api';
import { DurationUnit, NONE, UserType } from '@fullstory/search-types';
import * as segment from '@fullstory/proto/fs/api/segment';
import { FieldScope, VarType } from '@fullstory/search-vars';
import { dateConverter } from './helpers';

export type UserConverterMap = ConverterMap<UserType>;

const firstSeen: UserConverterMap[segment.UserFilter.filter_keys.firstSeen] = dateConverter(
  segment.UserFilter.filter_keys.firstSeen,
  'UserCreated',
);
const lastSeen: UserConverterMap[segment.UserFilter.filter_keys.lastSeen] = dateConverter(
  segment.UserFilter.filter_keys.lastSeen,
  'UserTipLastSessionStart',
);

const email = helpers.legacyStringOperatorConverter<UserType, segment.UserFilter.filter_keys.email>(
  segment.UserFilter.filter_keys.email,
  'UserEmail',
  0,
);

const name = helpers.legacyStringOperatorConverter<UserType, segment.UserFilter.filter_keys.name>(
  segment.UserFilter.filter_keys.name,
  'UserDisplayName',
  0,
);

// The SearchType for userId needs to be explicitly typed so that the criteria adjuster below is scoped to the
// correct types preventing the compiler from complaining about it.
const userId = helpers.legacyStringOperatorConverter<
  UserType,
  segment.UserFilter.filter_keys.userId
>(segment.UserFilter.filter_keys.userId, 'UserAppKey', 0, undefined, false);

const totalActiveTime = helpers.durationOperatorConverter<
  UserType,
  segment.UserFilter.filter_keys.totalActiveTime
>(segment.UserFilter.filter_keys.totalActiveTime, 'UserTipActiveSec', 0, DurationUnit.second);

const totalTime = helpers.durationOperatorConverter<
  UserType,
  segment.UserFilter.filter_keys.totalTime
>(segment.UserFilter.filter_keys.totalTime, 'UserTipTotalSec', 0, DurationUnit.second);

const averageSessionActiveTime = helpers.durationOperatorConverter<
  UserType,
  segment.UserFilter.filter_keys.averageSessionActiveTime
>(
  segment.UserFilter.filter_keys.averageSessionActiveTime,
  'UserTipAvgActiveSec',
  0,
  DurationUnit.second,
);

const averageSessionLength = helpers.durationOperatorConverter<
  UserType,
  segment.UserFilter.filter_keys.averageSessionLength
>(
  segment.UserFilter.filter_keys.averageSessionLength,
  'UserTipAvgSessionSec',
  0,
  DurationUnit.second,
);

const totalSessions = helpers.numericConverter<
  UserType,
  segment.UserFilter.filter_keys.totalSessions
>(segment.UserFilter.filter_keys.totalSessions, 'UserTipNumSessions', 0);

const userVarString = customvars.customVarString<
  UserType,
  segment.UserFilter.filter_keys.userVarString
>(segment.UserFilter.filter_keys.userVarString, FieldScope.User, VarType.Str);

const userVarStrings = customvars.customVarString<
  UserType,
  segment.UserFilter.filter_keys.userVarStrings
>(segment.UserFilter.filter_keys.userVarStrings, FieldScope.User, VarType.Strs);

const userVarInt = customvars.customVarNumbers<UserType, segment.UserFilter.filter_keys.userVarInt>(
  segment.UserFilter.filter_keys.userVarInt,
  FieldScope.User,
  VarType.Int,
);

const userVarInts = customvars.customVarNumbers<
  UserType,
  segment.UserFilter.filter_keys.userVarInts
>(segment.UserFilter.filter_keys.userVarInts, FieldScope.User, VarType.Ints);

const userVarFloat = customvars.customVarNumbers<
  UserType,
  segment.UserFilter.filter_keys.userVarFloat
>(segment.UserFilter.filter_keys.userVarFloat, FieldScope.User, VarType.Real);

const userVarFloats = customvars.customVarNumbers<
  UserType,
  segment.UserFilter.filter_keys.userVarFloats
>(segment.UserFilter.filter_keys.userVarFloats, FieldScope.User, VarType.Reals);

const userVarDate = customvars.customVarDates<UserType, segment.UserFilter.filter_keys.userVarDate>(
  segment.UserFilter.filter_keys.userVarDate,
  FieldScope.User,
  VarType.Date,
);

const userVarDates = customvars.customVarDates<
  UserType,
  segment.UserFilter.filter_keys.userVarDates
>(segment.UserFilter.filter_keys.userVarDates, FieldScope.User, VarType.Dates);

const userVarBool = customvars.customVarBools<UserType, segment.UserFilter.filter_keys.userVarBool>(
  segment.UserFilter.filter_keys.userVarBool,
  FieldScope.User,
  VarType.Bool,
);

const userVarBools = customvars.customVarBools<
  UserType,
  segment.UserFilter.filter_keys.userVarBools
>(segment.UserFilter.filter_keys.userVarBools, FieldScope.User, VarType.Bools);

// UserConverterMap defines the specification to convert between the UI's
// representation of user tip search types and the server's representation.
export const UserConverterMap: UserConverterMap = {
  any: {
    [NONE]: {
      criteria: s => [],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({ kind: 'any', subkind: NONE, value: NONE }),
      extractFromPartialCriteria: () => null,
      searchTypeToFilter: () => null,
      filterToSearchType: () => null,
    },
  },
  [segment.UserFilter.filter_keys.email]: email,
  [segment.UserFilter.filter_keys.name]: name,
  [segment.UserFilter.filter_keys.userId]: userId,
  [segment.UserFilter.filter_keys.totalActiveTime]: totalActiveTime,
  [segment.UserFilter.filter_keys.totalTime]: totalTime,
  [segment.UserFilter.filter_keys.averageSessionActiveTime]: averageSessionActiveTime,
  [segment.UserFilter.filter_keys.averageSessionLength]: averageSessionLength,
  [segment.UserFilter.filter_keys.totalSessions]: totalSessions,
  [segment.UserFilter.filter_keys.firstSeen]: firstSeen,
  [segment.UserFilter.filter_keys.lastSeen]: lastSeen,
  [segment.UserFilter.filter_keys.signedUp]: {
    [segment.SignedUpOperator.operator_keys.signedUp]: {
      criteria: s => [
        { Field: 'UserAppKey', Op: s.value ? Op.SEARCH_OP_NEQ : Op.SEARCH_OP_EQ, Value: '' },
      ],
      withoutPartials: s => helpers.stripPartials(s, helpers.identity),
      extractFromCriteria: c => ({
        kind: segment.UserFilter.filter_keys.signedUp,
        subkind: segment.SignedUpOperator.operator_keys.signedUp,
        value: c[0] && c[0].Op === Op.SEARCH_OP_NEQ,
      }),
      extractFromPartialCriteria: c =>
        c[0] &&
        c[0].Field == 'UserAppKey' &&
        c[0].Value == '' && {
          kind: segment.UserFilter.filter_keys.signedUp,
          subkind: segment.SignedUpOperator.operator_keys.signedUp,
          value: c[0].Op === Op.SEARCH_OP_NEQ,
        },
      searchTypeToFilter: st => helpers.searchTypeToFilterMirror(st),
      filterToSearchType: sf => helpers.filterToSearchTypeMirror(sf),
    },
  },
  [segment.UserFilter.filter_keys.userVarString]: userVarString,
  [segment.UserFilter.filter_keys.userVarStrings]: userVarStrings,
  [segment.UserFilter.filter_keys.userVarInt]: userVarInt,
  [segment.UserFilter.filter_keys.userVarInts]: userVarInts,
  [segment.UserFilter.filter_keys.userVarFloat]: userVarFloat,
  [segment.UserFilter.filter_keys.userVarFloats]: userVarFloats,
  [segment.UserFilter.filter_keys.userVarDate]: userVarDate,
  [segment.UserFilter.filter_keys.userVarDates]: userVarDates,
  [segment.UserFilter.filter_keys.userVarBool]: userVarBool,
  [segment.UserFilter.filter_keys.userVarBools]: userVarBools,
};
